import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { Organisation, PricingPlan } from '../types';
import { useViewPreference } from '../hooks/useViewPreference';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { OrganisationStripeIndicator } from '../components/OrganisationStripeIndicator';
import { OrganisationDetailModal } from '../components/OrganisationDetailModal';
import { ErrorBanner } from '../components/ErrorBanner';
import { useErrorBanner } from '../hooks/useErrorBanner';
import {
  ExclamationTriangleIcon,
  Squares2X2Icon,
  TableCellsIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

// Currency symbol mapping
const getCurrencySymbol = (currency: string): string => {
  const symbols = {
    GBP: '£',
    USD: '$',
    EUR: '€',
  };
  return symbols[currency as keyof typeof symbols] || currency;
};

export const Organisations: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organisation | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<Organisation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    billingEmail: '',
    planId: '',
    enabled: true,
    billingMethod: 'automated' as 'automated' | 'invoice',
  });
  const [refreshingPlans, setRefreshingPlans] = useState(false);
  const [viewMode, setViewMode] = useViewPreference('organisations', 'grid');
  const [filter, setFilter] = useState<{ status: string }>({ status: 'all' });
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'name',
    direction: 'asc',
  });

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState<Organisation | null>(null);

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError } = useToast();

  // Error banner for inline form errors
  const {
    error: formError,
    errorType,
    showError: showFormError,
    clearError: clearFormError,
  } = useErrorBanner();

  // Add useEffect to scroll to form when it becomes visible
  useEffect(() => {
    if (showCreateForm || showEditForm) {
      // Use a small delay to ensure the form is rendered before scrolling
      const scrollTimeout = setTimeout(() => {
        const formElement = document.getElementById('organisation-form');
        if (formElement) {
          // Get the element's position relative to the document
          const elementRect = formElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;

          // Account for sticky navbar height (64px = h-16 in Tailwind) plus some padding
          const navbarHeight = 64;
          const padding = 20; // Additional padding for better visual spacing
          const offsetPosition = absoluteElementTop - navbarHeight - padding;

          // Smooth scroll to the calculated position
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 150); // Slightly increased delay to ensure proper rendering
      return () => clearTimeout(scrollTimeout);
    }
  }, [showCreateForm, showEditForm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgsData, plansData] = await Promise.all([
          api.getOrganisations({ status: 'all' }),
          api.getAllPlans(),
        ]);
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetched plans data:', plansData);
          console.log('Plans data type:', Array.isArray(plansData), typeof plansData);
        }
        setOrganisations(orgsData);
        setPricingPlans(plansData);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching data:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle URL parameter to auto-open organisation modal
  useEffect(() => {
    if (!loading && organisations.length > 0) {
      const orgId = searchParams.get('org');
      if (orgId) {
        const targetOrganisation = organisations.find((org) => org.id === orgId);
        if (targetOrganisation) {
          // Auto-open the modal for this organisation
          handleViewDetails(targetOrganisation);
          // Clear the URL parameter after opening the modal to avoid re-opening on refresh
          setSearchParams({});
        }
      }
    }
  }, [loading, organisations, searchParams, setSearchParams]);

  const refreshPlans = async () => {
    setRefreshingPlans(true);
    try {
      const plansData = await api.getAllPlans();
      if (process.env.NODE_ENV === 'development') {
        console.log('Refreshed plans data:', plansData);
      }
      setPricingPlans(plansData);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error refreshing plans:', error);
      }
      alert('Failed to refresh plans. Please try again.');
    } finally {
      setRefreshingPlans(false);
    }
  };

  const handleShowCreateForm = async () => {
    setShowCreateForm(true);
    setFormData({
      name: '',
      billingEmail: '',
      planId: '',
      enabled: true,
      billingMethod: 'automated',
    });
    // Refresh plans when opening the form to ensure latest data
    await refreshPlans();
  };

  const handleShowEditForm = async (org: Organisation) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      billingEmail: org.billingEmail,
      planId: org.planId,
      enabled: org.enabled ?? true,
      billingMethod: org.billingMethod || 'automated',
    });
    setShowEditForm(true);
    // Refresh plans when opening the form to ensure latest data
    await refreshPlans();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.billingEmail || !formData.planId) return;

    if (showEditForm && editingOrg) {
      // Update existing organisation
      setUpdating(true);
      try {
        // Check if plan is changing - if so, use assignPlan to set status to pending
        const isPlanChanging = editingOrg.planId !== formData.planId;

        let updatedOrg;
        if (isPlanChanging) {
          // Use assignPlan for plan changes to properly set pending status
          const result = await api.assignPlan(
            editingOrg.id,
            formData.planId,
            formData.billingMethod
          );
          updatedOrg = result.organisation;

          // Also update other fields separately if needed
          if (
            editingOrg.name !== formData.name ||
            editingOrg.billingEmail !== formData.billingEmail ||
            (editingOrg.enabled ?? true) !== formData.enabled ||
            editingOrg.billingMethod !== formData.billingMethod
          ) {
            updatedOrg = await api.updateOrganisation(updatedOrg.id, {
              name: formData.name,
              billingEmail: formData.billingEmail,
              enabled: formData.enabled,
              billingMethod: formData.billingMethod,
            });
          }

          if (result.customerPortalUrl) {
            showSuccess(
              'Plan Assigned',
              `Organisation updated and plan assigned! Customer portal: ${result.customerPortalUrl}`
            );
          } else {
            showSuccess(
              'Plan Assigned',
              'Organisation updated and plan assigned. Status set to pending - customer needs to activate via portal.'
            );
          }
        } else {
          // No plan change - use regular update
          updatedOrg = await api.updateOrganisation(editingOrg.id, {
            name: formData.name,
            billingEmail: formData.billingEmail,
            enabled: formData.enabled,
            billingMethod: formData.billingMethod,
          });
          showSuccess('Organisation Updated', 'Organisation updated successfully!');
        }

        setOrganisations(organisations.map((org) => (org.id === editingOrg.id ? updatedOrg : org)));
        setShowEditForm(false);
        setEditingOrg(null);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error updating organisation:', error);
        }
        const errorMsg =
          error instanceof Error
            ? error.message
            : 'Failed to update organisation. Please try again.';
        if (
          errorMsg.includes('API request failed') ||
          errorMsg.includes('409') ||
          errorMsg.includes('DUPLICATE')
        ) {
          showFormError(errorMsg);
        } else {
          showError('Update Failed', errorMsg);
        }
      } finally {
        setUpdating(false);
      }
    } else {
      // Create new organisation
      setCreating(true);
      try {
        const selectedPlan = pricingPlans.find((p) => p.id === formData.planId);
        if (!selectedPlan) throw new Error('Selected plan not found');

        // For new organizations, we need to use assignPlan directly since it creates
        // the organization and assigns the plan with proper pending status

        // First try to find a basic/free plan for temporary assignment, or use the selected plan
        // Prefer matching by lowest tier value, then fall back to name-based match
        const basicPlan =
          pricingPlans
            .filter((p) => p.tier !== undefined)
            .sort((a, b) => (a.tier ?? Infinity) - (b.tier ?? Infinity))[0] ||
          pricingPlans.find((p) => p.name.toLowerCase().includes('free')) ||
          selectedPlan;

        // Create basic organization first with a simple plan
        const basicOrg = await api.createOrganisation({
          name: formData.name,
          billingEmail: formData.billingEmail,
          planId: basicPlan.id,
          enabled: formData.enabled,
          billingMethod: formData.billingMethod,
        });

        let finalOrg = basicOrg;

        // If the selected plan is different from the temporary one, assign the actual plan
        if (formData.planId !== basicPlan.id) {
          const result = await api.assignPlan(basicOrg.id, formData.planId, formData.billingMethod);
          finalOrg = result.organisation;

          if (result.customerPortalUrl) {
            showSuccess(
              'Organisation Created',
              `Organisation created with plan assigned! Customer portal: ${result.customerPortalUrl}`
            );
          } else {
            showSuccess(
              'Organisation Created',
              'Organisation created with plan assigned. Status set to pending - customer needs to activate via portal.'
            );
          }
        } else {
          showSuccess('Organisation Created', 'Organisation created successfully!');
        }

        setOrganisations([...organisations, finalOrg]);
        setShowCreateForm(false);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error creating organisation:', error);
        }
        const errorMsg =
          error instanceof Error
            ? error.message
            : 'Failed to create organisation. Please try again.';
        if (
          errorMsg.includes('API request failed') ||
          errorMsg.includes('409') ||
          errorMsg.includes('DUPLICATE')
        ) {
          showFormError(errorMsg);
        } else {
          showError('Creation Failed', errorMsg);
        }
      } finally {
        setCreating(false);
      }
    }

    setFormData({
      name: '',
      billingEmail: '',
      planId: '',
      enabled: true,
      billingMethod: 'automated',
    });
  };

  const handleDeleteClick = async (org: Organisation) => {
    const canDelete = await api.canDeleteOrganisation(org.id);
    if (!canDelete.canDelete) {
      showError('Cannot Delete', canDelete.reason || 'Cannot delete this organisation.');
      return;
    }

    setOrgToDelete(org);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!orgToDelete) return;

    setDeleting(true);
    try {
      await api.deleteOrganisation(orgToDelete.id);
      setOrganisations(organisations.filter((org) => org.id !== orgToDelete.id));
      showSuccess('Organisation Deleted', 'Organisation deleted successfully!');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error deleting organisation:', error);
      }
      showError('Deletion Failed', 'Failed to delete organisation. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setOrgToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setOrgToDelete(null);
  };

  const resetForms = () => {
    setShowCreateForm(false);
    setShowEditForm(false);
    setEditingOrg(null);
    setFormData({
      name: '',
      billingEmail: '',
      planId: '',
      enabled: true,
      billingMethod: 'automated',
    });
    clearFormError(); // Clear any form errors when closing forms
  };

  // Stripe sync moved to /ops/sync in Task 53c.4. Operators reach it via
  // the Operations sidebar entry; OpsActionList there handles the
  // trigger + poll for the async job (54b pattern).

  // Detail modal handlers
  const handleViewDetails = (org: Organisation) => {
    setSelectedOrganisation(org);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedOrganisation(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-base-content mb-2">Organisations</h1>
            <p className="text-base-content/70">Manage all your organisations</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* View Toggle */}
            <div className="flex items-center bg-base-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-base-100 text-base-content shadow-sm'
                    : 'text-base-content/70 hover:text-base-content'
                }`}
                title="Grid view"
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'table'
                    ? 'bg-base-100 text-base-content shadow-sm'
                    : 'text-base-content/70 hover:text-base-content'
                }`}
                title="Table view"
              >
                <TableCellsIcon className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={handleShowCreateForm}
              disabled={showCreateForm}
              className={`${showCreateForm ? 'bg-base-content/40 cursor-not-allowed' : 'bg-primary hover:bg-primary/80'} text-white px-4 py-2 rounded-md font-medium transition-colors`}
            >
              Create Organisation
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Sorting */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="statusFilter"
              className="block text-sm font-medium text-base-content mb-1"
            >
              Status
            </label>
            <select
              id="statusFilter"
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Statuses</option>
              <option value="active">Enabled</option>
              <option value="disabled">Disabled</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="sortFilter"
              className="block text-sm font-medium text-base-content mb-1"
            >
              Sort By
            </label>
            <select
              id="sortFilter"
              value={`${sort.field}-${sort.direction}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split('-');
                setSort({ field, direction: direction as 'asc' | 'desc' });
              }}
              className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="created-asc">Created Date (Oldest)</option>
              <option value="created-desc">Created Date (Newest)</option>
            </select>
          </div>
        </div>
      </div>

      {(showCreateForm || showEditForm) && (
        <div id="organisation-form" className="bg-base-100 shadow rounded-lg p-6">
          <h2 className="text-xl font-bold text-base-content mb-4">
            {showEditForm ? 'Edit Organisation' : 'Create New Organisation'}
          </h2>

          <ErrorBanner
            error={formError}
            type={errorType}
            onClose={clearFormError}
            autoScroll={true}
          />

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-base-content mb-1">
                Organisation Name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="Enter organisation name"
                required
              />
            </div>

            <div>
              <label
                htmlFor="billingEmail"
                className="block text-sm font-medium text-base-content mb-1"
              >
                Billing Email
              </label>
              <input
                type="email"
                id="billingEmail"
                value={formData.billingEmail}
                onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="Enter billing email"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="planId" className="block text-sm font-medium text-base-content">
                  Pricing Plan
                </label>
                <button
                  type="button"
                  onClick={refreshPlans}
                  disabled={refreshingPlans}
                  className="text-xs text-primary hover:text-primary/80 disabled:text-primary/50 transition-colors"
                >
                  {refreshingPlans ? 'Refreshing...' : 'Refresh Plans'}
                </button>
              </div>
              <select
                id="planId"
                value={formData.planId}
                onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
                className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                required
              >
                <option value="">Select a plan</option>

                {/* Global Plans */}
                {pricingPlans.filter((plan) => plan.isGlobal ?? true).length > 0 && (
                  <optgroup label="Global Plans">
                    {pricingPlans
                      .filter((plan) => plan.isGlobal ?? true)
                      .map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} -{' '}
                          {getCurrencySymbol(
                            typeof plan.price === 'object' ? plan.price?.currency || 'GBP' : 'GBP'
                          )}
                          {typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0}/
                          {plan.billingCycle ||
                            (typeof plan.price === 'object' ? plan.price?.period : 'month')}
                        </option>
                      ))}
                  </optgroup>
                )}

                {/* Custom Plans */}
                {pricingPlans.filter((plan) => !(plan.isGlobal ?? true)).length > 0 && (
                  <optgroup label="Custom Plans">
                    {pricingPlans
                      .filter((plan) => {
                        // Only show custom plans that are either:
                        // 1. Unassigned (no assignedOrgId)
                        // 2. Already assigned to the current org being edited
                        // 3. Not assigned to any other org
                        const isCustomPlan = !(plan.isGlobal ?? true);
                        if (!isCustomPlan) return false;

                        const isUnassigned = !plan.assignedOrgId;
                        const isAssignedToCurrentOrg =
                          editingOrg && plan.assignedOrgId === editingOrg.id;
                        const isAssignedToOtherOrg =
                          plan.assignedOrgId &&
                          (!editingOrg || plan.assignedOrgId !== editingOrg.id);

                        return isUnassigned || isAssignedToCurrentOrg || !isAssignedToOtherOrg;
                      })
                      .map((plan) => {
                        const isAssignedToOtherOrg = Boolean(
                          plan.assignedOrgId &&
                            (!editingOrg || plan.assignedOrgId !== editingOrg.id) &&
                            organisations.some((org) => org.id === plan.assignedOrgId)
                        );
                        const assignedOrgName = plan.assignedOrgId
                          ? organisations.find((org) => org.id === plan.assignedOrgId)?.name
                          : null;

                        return (
                          <option key={plan.id} value={plan.id} disabled={isAssignedToOtherOrg}>
                            {plan.name} -{' '}
                            {getCurrencySymbol(
                              typeof plan.price === 'object' ? plan.price?.currency || 'GBP' : 'GBP'
                            )}
                            {typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0}/
                            {plan.billingCycle ||
                              (typeof plan.price === 'object' ? plan.price?.period : 'month')}
                            {plan.assignedOrgId &&
                              assignedOrgName &&
                              ` (Assigned to ${assignedOrgName})`}
                            {plan.assignedOrgId && !assignedOrgName && ` (Assigned)`}
                          </option>
                        );
                      })}
                  </optgroup>
                )}
              </select>
              <p className="text-xs text-base-content/70 mt-1">
                Debug: Found {pricingPlans.length} plans
              </p>
            </div>

            <div>
              <label
                htmlFor="billingMethod"
                className="block text-sm font-medium text-base-content mb-1"
              >
                Billing Method
              </label>
              <select
                id="billingMethod"
                value={formData.billingMethod}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    billingMethod: e.target.value as 'automated' | 'invoice',
                  })
                }
                className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="automated">Automated (Credit Card)</option>
                <option value="invoice">Invoice/PO</option>
              </select>
              <p className="text-xs text-base-content/70 mt-1">
                Automated billing charges credit cards automatically. Invoice billing requires
                manual payment processing.
              </p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="rounded border-base-300 text-primary shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-base-content">Organisation Enabled</span>
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={creating || updating}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                {showEditForm
                  ? updating
                    ? 'Updating...'
                    : 'Update Organisation'
                  : creating
                    ? 'Creating...'
                    : 'Create Organisation'}
              </button>
              <button
                type="button"
                onClick={resetForms}
                className="bg-neutral hover:bg-neutral/80 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Conditional rendering based on view mode */}
      {viewMode === 'grid' ? (
        // Grid View
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {organisations
            .filter((org) => {
              const isArchived = Boolean(org.archivedAt);
              if (filter.status === 'archived') return isArchived;
              if (isArchived) return false;
              return (
                filter.status === 'all' ||
                (filter.status === 'active' && org.enabled) ||
                (filter.status === 'disabled' && !org.enabled)
              );
            })
            .sort((a, b) => {
              let aValue: string | number = '';
              let bValue: string | number = '';

              if (sort.field === 'name') {
                aValue = a.name.toLowerCase();
                bValue = b.name.toLowerCase();
              } else if (sort.field === 'created') {
                aValue = new Date(a.createdAt).getTime();
                bValue = new Date(b.createdAt).getTime();
              }

              if (sort.direction === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
              } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
              }
            })
            .map((organisation) => {
              const plan = pricingPlans.find((p) => p.id === organisation.planId);
              return (
                <div
                  key={organisation.id}
                  className="p-6 bg-base-100 rounded-lg shadow-md border cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleViewDetails(organisation)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-bold text-xl text-base-content">{organisation.name}</h3>
                      <OrganisationStripeIndicator
                        stripeCustomerId={organisation.stripeCustomerId ?? undefined}
                        stripeSubscriptionId={organisation.stripeSubscriptionId ?? undefined}
                      />
                    </div>
                    {organisation.archivedAt ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                        Archived
                      </span>
                    ) : (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          organisation.enabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {organisation.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-base-content/70">
                      <span className="font-medium">Billing Email:</span>{' '}
                      {organisation.billingEmail}
                    </p>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-base-content/70">
                        Current Plan:
                      </span>
                      {plan ? (
                        <span className="text-sm text-base-content/70">
                          {plan.name} (
                          {getCurrencySymbol(
                            typeof plan.price === 'object' ? plan.price?.currency || 'GBP' : 'GBP'
                          )}
                          {typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0}/
                          {plan.billingCycle ||
                            (typeof plan.price === 'object' ? plan.price?.period : 'month')}
                          )
                        </span>
                      ) : organisation.planId ? (
                        <div className="flex items-center space-x-1 text-amber-600">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          <span className="text-sm font-medium">Invalid plan</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1 text-base-content/50">
                          <span className="text-sm font-medium">No plan assigned</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-base-content/70">
                      <span className="font-medium">Max Projects:</span>{' '}
                      {organisation.maxProjects || 'N/A'}
                    </p>
                    <p className="text-sm text-base-content/70">
                      <span className="font-medium">Current Projects:</span>{' '}
                      {organisation.currentProjects || 0}
                    </p>
                    <div className="flex items-center space-x-4">
                      <p className="text-sm text-base-content/70">
                        <span className="font-medium">Billing Method:</span>{' '}
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            organisation.billingMethod === 'automated'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {organisation.billingMethod === 'automated' ? 'Automated' : 'Invoice'}
                        </span>
                      </p>
                      {(organisation.planStatus && plan) || (organisation.planId && !plan) ? (
                        <p className="text-sm text-base-content/70">
                          <span className="font-medium">Plan Status:</span>{' '}
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              organisation.planStatus && plan
                                ? organisation.planStatus === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : organisation.planStatus === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : organisation.planStatus === 'suspended'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {organisation.planStatus && plan
                              ? organisation.planStatus.charAt(0).toUpperCase() +
                                organisation.planStatus.slice(1)
                              : 'Unassigned'}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    {organisation.monthlyUsage && (
                      <div>
                        <p className="text-sm font-medium text-base-content mb-1">Monthly Usage:</p>
                        <div className="text-xs text-base-content/70 space-y-1">
                          <div>Users: {organisation.monthlyUsage.users}</div>
                          <div>Storage: {organisation.monthlyUsage.storageGB}GB</div>
                          <div>Bandwidth: {organisation.monthlyUsage.bandwidthGB}GB</div>
                          <div>Retention: {organisation.monthlyUsage.retentionDays} days</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-base-300 flex justify-between items-center">
                    <p className="text-xs text-base-content/70">
                      Created: {new Date(organisation.createdAt).toLocaleDateString()}
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(organisation);
                        }}
                        className="px-3 py-1 text-xs font-medium text-indigo-600 border border-indigo-600 rounded hover:bg-indigo-50 transition-colors flex items-center space-x-1"
                        title="View details"
                      >
                        <EyeIcon className="h-3 w-3" />
                        <span>View</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowEditForm(organisation);
                        }}
                        disabled={showEditForm && editingOrg?.id === organisation.id}
                        className="px-3 py-1 text-xs font-medium text-blue-600 border border-primary rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(organisation);
                        }}
                        disabled={deleting}
                        className="px-3 py-1 text-xs font-medium text-red-600 border border-red-600 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        // Table View
        <div className="bg-base-100 shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-base-300">
            <thead className="bg-base-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Billing Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Plan Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Projects
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-base-100 divide-y divide-base-300">
              {organisations
                .filter((org) => {
                  const isArchived = Boolean(org.archivedAt);
                  if (filter.status === 'archived') return isArchived;
                  if (isArchived) return false;
                  return (
                    filter.status === 'all' ||
                    (filter.status === 'active' && org.enabled) ||
                    (filter.status === 'disabled' && !org.enabled)
                  );
                })
                .sort((a, b) => {
                  let aValue: string | number = '';
                  let bValue: string | number = '';

                  if (sort.field === 'name') {
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                  } else if (sort.field === 'created') {
                    aValue = new Date(a.createdAt).getTime();
                    bValue = new Date(b.createdAt).getTime();
                  }

                  if (sort.direction === 'asc') {
                    return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
                  } else {
                    return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
                  }
                })
                .map((organisation) => {
                  const plan = pricingPlans.find((p) => p.id === organisation.planId);
                  return (
                    <tr
                      key={organisation.id}
                      className="hover:bg-base-200 cursor-pointer"
                      onClick={() => handleViewDetails(organisation)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="text-sm font-medium text-base-content">
                            {organisation.name}
                          </div>
                          <OrganisationStripeIndicator
                            stripeCustomerId={organisation.stripeCustomerId ?? undefined}
                            stripeSubscriptionId={organisation.stripeSubscriptionId ?? undefined}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {organisation.archivedAt ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Archived
                          </span>
                        ) : (
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              organisation.enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {organisation.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">
                        {organisation.billingEmail}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {plan ? (
                          <div className="text-sm text-base-content">
                            <div className="font-medium">{plan.name}</div>
                            <div className="text-base-content/70">
                              {getCurrencySymbol(
                                typeof plan.price === 'object'
                                  ? plan.price?.currency || 'GBP'
                                  : 'GBP'
                              )}
                              {typeof plan.price === 'number'
                                ? plan.price
                                : plan.price?.amount || 0}
                              /
                              {plan.billingCycle ||
                                (typeof plan.price === 'object' ? plan.price?.period : 'month')}
                            </div>
                          </div>
                        ) : organisation.planId ? (
                          <div className="flex items-center space-x-1 text-amber-600">
                            <ExclamationTriangleIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">Invalid plan</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-1 text-base-content/50">
                            <span className="text-sm font-medium">No plan assigned</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {organisation.planStatus && plan ? (
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              organisation.planStatus === 'active'
                                ? 'bg-green-100 text-green-800'
                                : organisation.planStatus === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : organisation.planStatus === 'suspended'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {organisation.planStatus.charAt(0).toUpperCase() +
                              organisation.planStatus.slice(1)}
                          </span>
                        ) : organisation.planId && !plan ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                            Unassigned
                          </span>
                        ) : (
                          <span className="text-sm text-base-content/50">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">
                        <div>
                          {organisation.currentProjects || 0} / {organisation.maxProjects || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                        {new Date(organisation.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(organisation);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 flex items-center space-x-1"
                            title="View details"
                          >
                            <EyeIcon className="h-4 w-4" />
                            <span>View</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowEditForm(organisation);
                            }}
                            disabled={showEditForm && editingOrg?.id === organisation.id}
                            className="text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(organisation);
                            }}
                            disabled={deleting}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {organisations.length === 0 && (
        <div className="bg-base-200 rounded-lg p-8 text-center">
          <p className="text-base-content/70 mb-4">No organisations found</p>
          <button
            onClick={() => setShowCreateForm(true)}
            disabled={showCreateForm}
            className={`${showCreateForm ? 'bg-base-content/40 cursor-not-allowed' : 'bg-primary hover:bg-primary/80'} text-white px-4 py-2 rounded-md font-medium transition-colors`}
          >
            Create Your First Organisation
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Organisation"
        message={`Are you sure you want to delete "${orgToDelete?.name}"? This action cannot be undone.`}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmDisabled={deleting}
      />

      {/* Organisation Detail Modal */}
      <OrganisationDetailModal
        organisation={selectedOrganisation}
        isOpen={showDetailModal}
        onClose={handleCloseDetailModal}
        onArchiveStateChange={(updated) => {
          setSelectedOrganisation(updated);
          setOrganisations((prev) => prev.map((org) => (org.id === updated.id ? updated : org)));
          showSuccess(updated.archivedAt ? `${updated.name} archived` : `${updated.name} restored`);
        }}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default Organisations;
